import React, { useContext } from "react";
import "./Monthly.css";
import { Context } from "../../../context/Context";
import { createGrantTotalPercent, createServiceChargePercent, formatter, formatter2 } from "../../../utils/helpers";
import { translations } from "../../../utils/lang";
import { MonthlyExpand } from "./monthlyExpand/MonthlyExpand";
import { ArrowDown, ArrowUp } from "../../ui";
import { rowToggleProps } from "../reportUtils";
import usePagedReport from "../usePagedReport";
import { PagedDeleteDialog, PagedPanel, PagedRows, PagedStates } from "../PagedReportParts";

const KIND = "monthly";
const DETAIL_GROUP_BY = ["month", "app"];
const SUM_KEYS = [
  "completed", "completedAmount", "rejected", "rejectedAmount", "canceled", "canceledAmount", "endurance", "refund", "ccAmount", "deliveryOffer", "keetaDeliveryOffer",
  "offers", "paidCampains", "serviceCharge", "enduranceAmount", "refundAmount", "posDevice", "vat", "grandTotal",
];
// A store row of the server summary with every total present as a number (the screens format them with toFixed).
const totalsOf = (store) => Object.fromEntries([...SUM_KEYS.map((key) => [key, Number(store[key]) || 0]), ["store", store.store]]);

// Monthly table with server-side filtering, sorting and paging (PAGED_REPORTS). Same cards, actions and permissions as Monthly.jsx; the browser holds
// one page of one store at a time and prints the server's totals for every header.
export const MonthlyPaged = ({ active = true }) => {
  const { lang } = useContext(Context);
  const report = usePagedReport({ kind: KIND, detailGroupBy: DETAIL_GROUP_BY, amountField: "completedAmount", active });
  const { text, stores, openStore } = report;
  const titles = translations[lang].reports.monthly;

  const fields = [
    { key: "app", filterKey: "apps", onChange: (selected) => report.update({ apps: selected }) },
    { key: "store", filterKey: "stores", onChange: report.changeStores },
    { key: "branch", filterKey: "branches", onChange: (selected) => report.update({ branches: selected }) },
  ];

  return (
    <div className="report-monthly-table">
      <PagedPanel report={report} prefix="monthly" titles={titles.inputTitles} fields={fields} />

      <div className="report-monthly-card-col">
        <PagedStates report={report} />
        {stores.map((store) => {
          const id = String(store.storeId);
          const isExpanded = openStore === id;
          const summary = totalsOf(store);
          return (
            <React.Fragment key={id}>
              <div
                className={`report-monthly-summary-card ${
                  isExpanded ? "colored" : ""
                }  ${
                  store.pending > 0 ? "notApproved" : ""
                }`}
                {...rowToggleProps(isExpanded, () => report.toggleStore(id))}
              >
                <div className="report-monthly-grid-col">
                  <div className="report-monthly-p center">
                    <p>
                      <strong>{store.store}</strong>
                    </p>
                    {store.pending > 0 && <span className="report-pending-tag">{text.pendingApproval}</span>}
                  </div>
                </div>
                <div className="report-monthly-grid-col">
                  <div className="report-monthly-p">
                    <strong>
                      {translations[lang].reports.monthly.body[0]}
                    </strong>{" "}
                    {formatter2.format(summary.completed)} /{" "}
                    {formatter.format(summary.completedAmount.toFixed(2))}
                  </div>
                  <div className="report-monthly-p">
                    <strong>
                      {translations[lang].reports.monthly.body[1]}
                    </strong>{" "}
                    {formatter.format(summary.vat.toFixed(2))}
                  </div>
                  <div className="report-monthly-p">
                    <strong>
                      {translations[lang].reports.monthly.body[2]}
                    </strong>{" "}
                    (
                    {createGrantTotalPercent({
                      completedAmount: summary.completedAmount,
                      grandTotal: summary.grandTotal - summary.paidCampains,
                      offers: summary.offers,
                    })}
                    %) /{" "}
                    {formatter.format(
                      summary.grandTotal.toFixed(2) -
                        summary.paidCampains.toFixed(2),
                    )}
                  </div>
                </div>
                <div className="report-monthly-grid-col">
                  <div className="report-monthly-p">
                    <div className="report-monthly-p-grid">
                      <p>
                        <strong>
                          {translations[lang].reports.monthly.body[3]}
                        </strong>{" "}
                        {formatter2.format(summary.rejected)} /{" "}
                        {formatter.format(summary.rejectedAmount)}
                      </p>
                      <p>
                        <strong>
                          {translations[lang].reports.monthly.body[4]}
                        </strong>{" "}
                        {formatter2.format(summary.canceled)} /{" "}
                        {formatter.format(summary.canceledAmount)}
                      </p>
                    </div>
                  </div>
                  <div className="report-monthly-p">
                    <div className="report-monthly-p-grid">
                      <p>
                        <strong>
                          {translations[lang].reports.monthly.body[5]}
                        </strong>{" "}
                        {formatter2.format(summary.endurance)} /{" "}
                        {formatter.format(summary.enduranceAmount.toFixed(2))}
                      </p>
                      <p>
                        <strong>
                          {translations[lang].reports.monthly.body[6]}
                        </strong>{" "}
                        {formatter2.format(summary.refund)} /{" "}
                        {formatter.format(summary.refundAmount.toFixed(2))}
                      </p>
                    </div>
                  </div>
                  <div className="report-monthly-p">
                    <strong>
                      {translations[lang].reports.monthly.body[9]}
                    </strong>{" "}
                    {formatter.format(summary.ccAmount.toFixed(2))}
                  </div>
                </div>
                <div className="report-monthly-grid-col">
                  <div className="report-monthly-p">
                    <strong>
                      {translations[lang].reports.monthly.body[7]}
                    </strong>{" "}
                    (
                    {createServiceChargePercent({
                      completedAmount: summary.completedAmount,
                      serviceCharge: summary.serviceCharge,
                      offers: summary.offers,
                      deliveryOffer: summary.keetaDeliveryOffer,
                    })}
                    %) / {formatter.format(summary.serviceCharge.toFixed(2))}
                  </div>
                  <div className="report-monthly-p">
                    <div className="report-monthly-p-grid">
                      <p>
                        <strong>
                          {translations[lang].reports.monthly.body[8]}
                        </strong>
                        (
                        {summary.completed > 0
                          ? (summary.deliveryOffer / summary.completed).toFixed(
                              2,
                            )
                          : 0}
                        ) / {formatter.format(summary.deliveryOffer.toFixed(2))}
                      </p>
                      <p>
                        <strong>
                          {translations[lang].reports.monthly.body[10]}
                        </strong>
                        (
                        {summary.completed > 0
                          ? (summary.offers / summary.completed).toFixed(2)
                          : 0}
                        ) / {formatter.format(summary.offers.toFixed(2))}
                      </p>
                    </div>
                  </div>
                  <div className="report-monthly-p">
                    <strong>
                      {translations[lang].reports.monthly.body[11]}
                    </strong>{" "}
                    {formatter.format(summary.paidCampains.toFixed(2))}
                  </div>
                </div>
                <div className="report-expand-icon">
                  {isExpanded ? <ArrowUp size={18} /> : <ArrowDown size={18} />}
                </div>
              </div>
              {isExpanded && (
                <PagedRows report={report}>
                  {(anchors, groupRows, hierarchy) => <MonthlyExpand filteredData={anchors} groups={groupRows} paged={hierarchy} onRefresh={report.refresh} />}
                </PagedRows>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <PagedDeleteDialog report={report} kind={KIND} />
    </div>
  );
};
